// useUsage.ts
// Tracks monthly evaluation usage per user.
// User identity comes from Clerk, usage data stored in Supabase.

import { useState, useEffect, useCallback } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import { makeSupabaseClient } from '../lib/supabase';
import { TIERS } from '../lib/constants';
import type { TierKey } from '../types';

interface UseUsageResult {
  used: number;
  limit: number;
  remaining: number;
  canEvaluate: boolean;
  loading: boolean;
  incrementUsage: () => Promise<void>;
  refetch: () => void;
}

function getCurrentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function useUsage(tier: TierKey): UseUsageResult {
  const { user } = useUser();
  const { getToken } = useAuth();
  const [used, setUsed] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const limit = TIERS[tier].monthlyLimit;
  const remaining = limit === Infinity ? Infinity : Math.max(0, limit - used);
  const canEvaluate = remaining > 0;

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    async function fetchUsage() {
      setLoading(true);
      const month = getCurrentMonth();
      const token = await getToken({ template: 'Supabase' });
      const supabase = makeSupabaseClient(token ?? null);

      const { data, error } = await supabase
        .from('usage')
        .select('evaluation_count')
        .eq('user_id', user!.id)
        .eq('month', month)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Failed to fetch usage:', error);
      }

      setUsed(data?.evaluation_count || 0);
      setLoading(false);
    }

    fetchUsage();
  }, [user, getToken, refreshKey]);

  const incrementUsage = useCallback(async () => {
    if (!user) return;
    const month = getCurrentMonth();
    const token = await getToken({ template: 'Supabase' });
    const supabase = makeSupabaseClient(token ?? null);

    const { error } = await supabase.rpc('increment_usage', {
      p_user_id: user.id,
      p_month: month,
    });

    if (error) {
      console.error('Failed to increment usage:', error);
      return;
    }

    setUsed(prev => prev + 1);
  }, [user, getToken]);

  return {
    used,
    limit,
    remaining,
    canEvaluate,
    loading,
    incrementUsage,
    refetch: () => setRefreshKey(k => k + 1),
  };
}